import { useState } from 'react';

import { useFormMutation } from '@/api/setters';
import Button from '@/components/ui/Button';
import TitledCard from '@/components/ui/TitledCard';
import { formMutationDto } from '@/dto/formMutationDto';
import OrderSummary from '@/features/v2/hh/components/OrderSummary';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';
import cn from '@/utils/cn';

import AddonsCheckboxGroup from '../components/AddonsCheckboxGroup';
import DocumentTypeSelect from '../components/DocumentTypeSelect';
import { EcommerceStepForm } from '../components/EcommerceStepForm';
import EditPlotInfoModal from '../components/layout/EditPlotInfoModal';
import {
  ADDON_PRICES,
  ADDON_TYPE,
  DOCUMENT_PRICES,
  DOCUMENT_TYPE,
  LINE_TYPE,
  PLOT_TYPE,
} from '../schema';
import { DOCUMENT_STEP } from '../stepsSchema';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

const LINE_TYPE_LABELS: Record<string, string> = {
  [LINE_TYPE.LOW]: 'Niskie napięcie',
  [LINE_TYPE.MEDIUM]: 'Średnie napięcie',
  [LINE_TYPE.HIGH]: 'Wysokie napięcie',
  [LINE_TYPE.TRANSMISSION]: 'Linia przesyłowa',
  [LINE_TYPE.TRANSFORMER]: 'Słup z transformatorem',
};

const PLOT_TYPE_LABELS: Record<string, string> = {
  [PLOT_TYPE.AGRICULTURAL]: 'Rolna',
  [PLOT_TYPE.RESIDENTIAL_BUILDING]: 'Budowlana mieszkaniowa',
  [PLOT_TYPE.SERVICE_BUILDING]: 'Budowlana usługowa',
  [PLOT_TYPE.INDUSTRIAL]: 'Przemysłowa',
  [PLOT_TYPE.FOREST]: 'Leśna',
  [PLOT_TYPE.NO_MPZP]: 'Brak MPZP',
  [PLOT_TYPE.UNKNOWN]: 'Nie wiem',
};

interface SummaryRowProps {
  label: string;
  value: string;
  className?: string;
}

const SummaryRow = ({ label, value, className }: SummaryRowProps) => (
  <div
    className={cn('flex items-center justify-between gap-4 py-3', className)}
  >
    <span className="caption-large shrink-0 whitespace-nowrap text-dark-gray-500">
      {label}
    </span>
    <span className="caption-large-semi flex-1 text-right text-dark-gray-500">
      {value}
    </span>
  </div>
);

const Step3DocumentSelect = () => {
  const { i18n } = useLingui();
  const { setValue, watch, trigger, getValues } = useFormContext();
  const { step, innerStep, innerObject, nextStep, nextInnerStep } =
    useStepper();
  const formMutation = useFormMutation();

  const { lastStep } = DOCUMENT_STEP.lastStep(watch());
  const showAddons = innerStep >= lastStep;
  const [isEditOpen, setIsEditOpen] = useState(false);

  const lineTypes = watch('line_types');
  const plotType = watch('plot_type');
  const coOwnersCount = watch('co_owners_count');

  const lineTypesSummary =
    ((lineTypes as string[]) ?? [])
      .map((v) => LINE_TYPE_LABELS[v])
      .filter(Boolean)
      .join(', ') || '—';
  const plotTypeSummary = PLOT_TYPE_LABELS[plotType] ?? '—';
  const coOwnersSummary = coOwnersCount || '—';

  const handleSave = (values: {
    line_types: string[];
    plot_type: string;
    co_owners_count: string;
  }) => {
    setValue('line_types', values.line_types, { shouldDirty: true });
    setValue('plot_type', values.plot_type, { shouldDirty: true });
    setValue('co_owners_count', values.co_owners_count, {
      shouldDirty: true,
    });
  };

  const handleNext = async () => {
    const valid = await trigger(DOCUMENT_STEP.fields(innerStep));
    if (!valid) return;

    if (!showAddons) {
      nextInnerStep();
      scrollToTop();
      return;
    }

    await formMutation.mutateAsync({
      ...formMutationDto({
        form_data: getValues(),
        stepper: {
          step: step + 1,
          innerStep: { ...innerObject, [step + 1]: 0 },
        },
      }),
    });
    nextStep();
    scrollToTop();
  };

  return (
    <EcommerceStepForm
      backButton={!!DOCUMENT_STEP.backButton?.(innerStep)}
      nextButton={{
        label: showAddons
          ? i18n.t('Przejdź do płatności')
          : i18n.t('Przejdź do dodatków'),
        handleSubmit: handleNext,
      }}
    >
      <EditPlotInfoModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSave={handleSave}
        currentValues={{
          line_types: lineTypes,
          plot_type: plotType,
          co_owners_count: coOwnersCount,
        }}
      />

      {!showAddons && (
        <TitledCard
          title={i18n.t('Wybierz dokument')}
          className="p-[14px] md:p-6"
        >
          <DocumentTypeSelect />

          <div className="divide-y divide-dark-gray-100 rounded-md bg-gray-200 p-[14px] md:p-6">
            <div className="flex items-center justify-between gap-3 pb-3">
              <p className="h5 text-dark-gray-500">
                {i18n.t('Informacje o działce')}
              </p>

              <Button
                type="button"
                size="small"
                onClick={() => setIsEditOpen(true)}
                className="shrink-0"
              >
                {i18n.t('Edytuj')}
              </Button>
            </div>

            <SummaryRow
              label={i18n.t('Typ napięcia')}
              value={lineTypesSummary}
            />

            <SummaryRow
              label={i18n.t('Rodzaj działki')}
              value={plotTypeSummary}
            />

            <SummaryRow
              label={i18n.t('Liczba współwłaścicieli')}
              value={coOwnersSummary}
              className="pb-0"
            />
          </div>
        </TitledCard>
      )}

      {showAddons && (
        <TitledCard
          title={i18n.t('Wybierz dodatkowe opcje')}
          className="gap-3 p-[14px] md:p-6"
        >
          <AddonsCheckboxGroup />

          <OrderSummary
            documentTypes={[
              {
                value: DOCUMENT_TYPE.TEMPLATE,
                title: i18n.t('Szablon do samodzielnego wypełnienia'),
                price: DOCUMENT_PRICES[DOCUMENT_TYPE.TEMPLATE],
              },
              {
                value: DOCUMENT_TYPE.ONLINE,
                title: i18n.t('Dokument wypełniany online'),
                price: DOCUMENT_PRICES[DOCUMENT_TYPE.ONLINE],
              },
            ]}
            addons={[
              {
                value: ADDON_TYPE.PDF_INSTRUCTION,
                title: i18n.t('Instrukcja PDF'),
                price: ADDON_PRICES[ADDON_TYPE.PDF_INSTRUCTION],
              },
              {
                value: ADDON_TYPE.CONSULTATION,
                title: i18n.t('Konsultacja z prawnikiem'),
                price: ADDON_PRICES[ADDON_TYPE.CONSULTATION],
              },
            ]}
          />
        </TitledCard>
      )}
    </EcommerceStepForm>
  );
};

export default Step3DocumentSelect;
